/**
 * An abstract implementation of MapLayerBase which provides much of the functionality needed to conform to the interface defined by MapLayerBase, in regard to modifiable data.
 *
 * Concrete implementations should provide overrides of `createDataModel`, `attachLayer`, `canDraw`, and `isEditable`.
 *
 * ## Events
 * This class supplies the following events for inheritors.
 * Name | Arguments | Description
 * activate | The draw mode | Fired whenever the draw mode is activated for this layer.
 * deactivate | None | Fired whenever the draw mode is deactivated for this layer.
 * model-added | The geometry model, the data model | Fired whenever a model is added to this layer.
 * model-removed | The geometry model, the data model | Fired whenever a model is removed from this layer.
 * models-selected | The precipitating event, the label, the associated map point, and the set of geometry models selected | Fired whenever a model or set of models is selected
 * @module app/models/map/EditableLayerBase
 */
import SymbolFactory from "app/models/map/SymbolFactory";
import MapLayerBase from "app/models/map/base/MapLayerBase";
import _ from "underscore";
import Field from "core/models/Field";
import Geometry from "app/models/map/Geometry";
import Feature from "app/models/Feature";
import ErrorUtils from "app/utils/ErrorUtils";
import _cloneDeep from "lodash.clonedeep";
import _remove from "lodash.remove";
var shapeCount = 0;

export default MapLayerBase.extend(
  {
    /**
     * Initializes the layer.
     * @param  {object} options The options map.
     * @param {SymbolFactory} [options.symbolFactory] The symbol factory to use for object rendering.
     */
    initialize: function (options) {
      this.options = _.defaults({}, options, {
        symbolFactory: SymbolFactory.default(),
      });

      this._factory = this.options.symbolFactory;

      this._features = {};

      this._graphics = [];
      this._highlighted = [];
    },

    attach: function (map) {
      var self = this;

      self._layer = self.attachLayer(map);

      return this.load().done(function () {
        var visible = self.visible;

        if (self.serviceLayer) {
          visible = visible && self.serviceLayer.visible;
        }

        self._layer.setVisibility(visible && self.areParentsVisible());

        self.trigger("attach");
      });
    },

    hasSymbolFactory: function () {
      return true;
    },

    /**
     * Gets the symbol factory associated with this layer.
     * @return {SymbolFactory} The symbol factory.
     */
    getSymbolFactory: function () {
      return this._factory;
    },

    /**
     * Updates the appearance based on the given `symbolFactory`.
     * @param {SymbolFactory} symbolFactory The symbol factory.
     * @private
     */
    _onSymbolFactoryChanged: function (symbolFactory) {
      _.each(
        this._graphics,
        function (graphic) {
          if (graphic._oldSymbol) {
            //if it's highlighted, let it stay highlighted and change what it'll revert to
            graphic._oldSymbol = symbolFactory.byType(graphic.geometry.type);
          } else {
            graphic.setSymbol(symbolFactory.byType(graphic.geometry.type));
          }
        },
        this
      );
    },

    /**
     * Attach the core esri layer associated with this layer to the map.
     * @param  {Map} map The map to which to attach.
     * @return {esri/layers/GraphicsLayer | esri/layers/FeatureLayer}         The core layer that was attached.
     */
    attachLayer: function (map) {
      return this.getMapLayer();
    },

    /**
     * Sets the symbol factory associated with this layer.
     * @param {SymbolFactory} symbolFactory The symbol factory.
     */
    setSymbolFactory: function (symbolFactory) {
      if (this._factory) {
        this._factory.off("change", this._onSymbolFactoryChanged, this);
      }

      var factoryUpdated;

      if (this._factory != symbolFactory) {
        factoryUpdated = true;
      }

      this._factory = symbolFactory;

      if (this._factory) {
        this._factory.on("change", this._onSymbolFactoryChanged, this);

        this._onSymbolFactoryChanged(this._factory);
        if (factoryUpdated) {
          this.trigger("change:symbolFactory");
        }
      }
    },

    canDraw: function () {
      return true;
    },

    getName: function () {
      return "Workspace";
    },

    getMapLayer: function () {
      if (!this._layerCreated) {
        this._layer = this.createMapLayer();
        this._layerCreated = true;
      }

      return this._layer;
    },

    /**
     * Used by `EditableLayerBase` to initially generate the layer returned by `getMapLayer`.
     * @return {esri.layers.GraphicsLayer} The layer to associate with this instance. Only called once per instance.
     */
    createMapLayer: function () {
      return null;
    },

    isEditable: function () {
      return true;
    },

    _geometryFromData: function (data) {
      var result = null;

      if (data) {
        result = data.getFeature().getGeometry();
      }

      result.originalLayer = this;

      return result;
    },

    _dataFromGeometry: function (geometry) {
      var result = null;

      if (geometry) {
        result = geometry.getFeature().getProperties();
      }

      result.originalLayer = this;

      return result;
    },

    _addPair: function (dataModel, geometryModel) {
      if (geometryModel) {
        var feature = geometryModel.getFeature();

        if (!feature) {
          if (dataModel && dataModel.getFeature()) {
            feature = dataModel.getFeature();
            feature.setGeometry(geometryModel);
          } else {
            feature = Feature.generate(geometryModel, dataModel, {
              featureLayer: this.options.featureLayer,
            });
          }
        } else {
          feature.setProperties(dataModel);
        }

        this._features[feature.cid] = feature;
      }
    },

    _removePair: function (dataModel, geometryModel) {
      if (geometryModel) {
        delete this._features[geometryModel.getFeature().cid];
      }
    },

    /**
     * Creates a data model to associate with the given geometry.
     * @param  {app/models/map/Geometry} geometryModel The geometry model.
     * @return {object}               The data model.
     */
    createDataModel: function (geometryModel) {
      var feature =
        geometryModel.getFeature() ||
        Feature.generate(
          geometryModel,
          {},
          {
            featureLayer: this.options.featureLayer,
          }
        );

      return feature.getProperties();
    },

    /**
     * Adds an entry to this layer.
     * @param {app/models/map/Geometry} geometryModel The geometry model.
     * @param {object} [dataModel]     The data model.
     */
    addObject: function (geometryModel, dataModel) {
      if (!dataModel) {
        dataModel = this.createDataModel(geometryModel);
      }

      this._addPair(dataModel, geometryModel);

      return dataModel;
    },

    /**
     * Removes an entry from this layer.
     * @param  {app/models/map/Geometry} geometryModel The geometry model.
     * @return {object}               The data model that was removed.
     */
    removeObject: function (geometryModel) {
      var data = this._dataFromGeometry(geometryModel);

      this._removePair(data, geometryModel);

      return data;
    },

    dataForGeometry: function (geometryModel) {
      return this._dataFromGeometry(geometryModel);
    },

    geometryForData: function (data) {
      return this._geometryFromData(data);
    },

    /**
     * Adds the given geometry model to the layer.
     * @param {app/models/map/Geometry} geometry The geometry to add.
     * @param {object} [data]     The data to add.
     */
    add: function (geometry, data) {
      if (geometry) {
        var label = this.createLabel(geometry);

        if (this.layer) {
          //basically, if we aren't testing
          this.layer.add(label);
        }

        this._graphics.push(label);
        data = this.addObject(geometry, data);
        label.setAttributes(data.attributes);
        this._attachProperties(data);
        this.trigger("model-added", geometry, data);
      }

      return data;
    },

    /**
     * Attaches event handlers to a given properties object.
     * @param {FeatureData} props The properties to attach.
     * @private
     */
    _attachProperties: function (props) {
      props.on("change", this._onPropertiesChange, this);
    },

    /**
     * Detaches event handlers from a given properties object.
     * @param {FeatureData} props The properties to detach.
     * @private
     */
    _detachProperties: function (props) {
      props.off("change", this._onPropertiesChange, this);
    },

    /**
     * Handles property change events.
     * @param {FeatureData} props The properties that changed.
     * @private
     */
    _onPropertiesChange: function (props) {
      var label = this._getLabelFromModel(this._geometryFromData(props));

      label.setAttributes(props.attributes);
    },

    /**
     * Adds the given map geometry to the layer.
     * @param {Geometry} mapModel The ESRI Geometry to add to the layer.
     */
    addGeometry: function (mapModel) {
      if (mapModel) {
        var geometry = this._createModel(mapModel);
        var label = this._createLabelFromMapModel(mapModel);

        label._model = geometry;
        this.layer.add(label);
        this._graphics.push(label);
        var data = this.addObject(geometry);
        this._attachProperties(data);
        this.trigger("model-added", geometry, data);
      }
    },

    /**
     * Removes the given *geometry* model(s) from the layer.
     * @param  {Array | object} models The geometry model(s) to remove.
     * @return {object}        If a single geometry model is removed, the associated data model.
     */
    remove: function (models) {
      if (!_.isArray(models)) {
        return this._removeModel(models);
      } else {
        _.each(
          models,
          function (model) {
            this._removeModel(model);
          },
          this
        );
      }
    },

    _removeModel: function (model) {
      var models = this.getGeometries();
      var labels = this.getLabels();
      var removed;

      if (model) {
        var modelIndex = models.indexOf(this.toGeometry(model));

        if (modelIndex != -1) {
          var label = labels[modelIndex];
          var data = this.removeObject(this.toGeometry(model));

          this.layer.remove(label);
          labels.splice(modelIndex, 1);
          models.splice(modelIndex, 1);

          removed = data;
          this._detachProperties(data);
          this.trigger("model-removed", model, data);
        }
      }

      return removed;
    },

    /**
     * Removes all models from the layer.
     */
    clear: function () {
      var models = this.getGeometries();

      this.remove(models.slice());
    },

    /**
     * Create a label from the given geometry model.
     * @param  {object} model The geometry model.
     * @return {esri/Graphic}       The label that was created.
     */
    createLabel: function (model) {
      var mapModel = this._createMapModel(model);
      var result = this._createLabelFromMapModel(mapModel);

      //Deep clone the original graphic's geometry so that we can always get back to the original;
      result._geometryOnLoad = _cloneDeep(result.geometry);
      result._model = model;

      return result;
    },

    _createLabelFromMapModel: function (mapModel) {
      var Graphic = window.require("esri/graphic");

      return new Graphic(mapModel, this._factory.byType(mapModel.type));
    },

    _createModel: function (mapModel) {
      return this._updateModel(new Geometry(), mapModel);
    },

    _updateModel: function (model, mapModel) {
      return model.parseEsriGeometry(mapModel);
    },

    _createMapModel: function (model) {
      return model.generateEsriGeometry();
    },

    /**
     * Gets the list of geometry models in this layer.
     * @return {Array} The list of geometry models.
     */
    getGeometries: function () {
      return _.pluck(this.getLabels(), "_model");
    },

    /**
     * Gets the geometry model at the specified index in this layer.
     * @param  {Integer} idx The index.
     * @return {object}     The geometry model.
     */
    getGeometry: function (idx) {
      return this.getLabel(idx)._model;
    },

    /**
     * Gets the list of labels in this layer.
     * @return {Array} The labels.
     */
    getLabels: function () {
      return this._graphics;
    },

    /**
     * Gets the label at the specified index in this layer.
     * @param  {Integer} idx The index.
     * @return {esri/Graphic}     The label.
     */
    getLabel: function (idx) {
      return this.getLabels()[idx];
    },

    _getModelFromLabel: function (label) {
      var labels = this.getLabels();
      var idx = labels.indexOf(label);

      if (idx !== -1) {
        return this.getGeometry(idx);
      } else {
        return null;
      }
    },

    _getLabelFromModel: function (model) {
      var models = this.getGeometries();
      var idx = models.indexOf(this.toGeometry(model));

      return this.getLabel(idx);
    },

    /**
     * Gets the label associated with the given geometry model.
     * @param  {object} model The geometry model.
     * @return {esri/Graphic}       The label.
     */
    getLabelForModel: function (model) {
      return this._getLabelFromModel(model);
    },

    toGraphic: function (model) {
      return this.getLabelForModel(this.toGeometry(model));
    },

    toFeature: function (model) {
      var feature = this.toGeometry(model).getFeature();

      feature.originalLayer = this;

      return feature;
    },

    getExtent: function () {
      var labels = this.getLabels();
      var geometry, extent, ext;
      var idx = 0;
      var Point = window.require("esri/geometry/Point"),
        Extent = window.require("esri/geometry/Extent");

      _.each(
        labels,
        function (graphic) {
          geometry = graphic.geometry;
          //credit: http://forums.esri.com/Thread.asp?c=158&f=2396&t=266110
          if (geometry instanceof Point) {
            ext = new Extent(
              geometry.x - 1,
              geometry.y - 1,
              geometry.x + 1,
              geometry.y + 1,
              geometry.spatialReference
            );
          } else if (geometry instanceof Extent) {
            ext = geometry;
          } else if (geometry) {
            ext = geometry.getExtent();
          }

          if (ext) {
            if (
              _.isNaN(ext.xmin) ||
              _.isNaN(ext.xmax) ||
              _.isNaN(ext.ymin) ||
              _.isNaN(ext.ymax)
            ) {
              console.debug(
                "Invalid extent generated at index " + idx + " of " + this.name
              );
            } else if (extent) {
              extent = extent.union(ext);
            } else {
              extent = new Extent(ext);
            }
          } else {
            //console.log('Problem getting extent at index: ' + idx);
          }

          idx++;
        },
        this
      );

      return extent;
    },

    _findModelsFromGeometryQuery: function (findFunction) {
      var models = [];
      var labels = this.getLabels();
      var modelList = this.getGeometries();

      for (var i = 0; i < labels.length; i++) {
        var geometry = labels[i].geometry;

        if (findFunction(geometry)) {
          models.push(modelList[i]);
        }
      }

      return models;
    },

    _findGraphicsFromModelQuery: function (findFunction) {
      var labels = [];
      var models = this.getGeometries();
      var labelList = this.getLabels();

      for (var i = 0; i < models.length; i++) {
        var model = models[i];

        if (findFunction(model)) {
          labels.push(labelList[i]);
        }
      }

      return labels;
    },

    findModelsFromLabel: function (label, point) {
      var GeometryEngine = window.require("esri/geometry/geometryEngine");
      var self = this;
      var findFunctionBase = function (geometry) {
        var overlappingPointsDistance = 0.8 * 0.0045; // 1.5 Meters. Chosen Arbitrarily;

        overlappingPointsDistance *= self.layer.getMap().getScale();

        return (
          GeometryEngine.distance(geometry, point, "meters") <
          overlappingPointsDistance
        );
      };
      var findFunction;

      if (label && label.geometry) {
        findFunction = function (geometry) {
          return geometry == label.geometry || findFunctionBase(geometry);
        };
      } else {
        findFunction = findFunctionBase;
      }

      var models = this._findModelsFromGeometryQuery(findFunction);

      models = _.map(
        models,
        _.bind(function (model) {
          return this.toData(model);
        }, this)
      );

      _.each(
        models,
        function (model) {
          model.originalLayer = this;
        },
        this
      );

      return models;
    },

    /**
     * Gets the labels associated with the objects in the given selector.
     * @private
     * @param  {Array|Object} selector An array or single item, which is either a "data" item or a "geometry" item.
     * @return {Array}          The array of labels corresponding to the data.
     */
    _labelsFromSelector: function (selector) {
      var targets = [];
      var self = this;

      if (selector) {
        if (!_.isArray(selector)) {
          selector = [selector];
        }

        _.each(selector, function (model) {
          var label = self.getLabelForModel(self.toGeometry(model));

          if (label) {
            targets.push(label);
          }
        });
      }

      return targets;
    },

    highlight: function (symbolFactory, selector) {
      var self = this;
      var targets = selector
        ? this._labelsFromSelector(selector)
        : this._graphics;

      _.each(targets, function (graphic) {
        var symbol = symbolFactory.byType(graphic.geometry.type);

        graphic._oldSymbol = graphic.symbol;
        graphic.setSymbol(symbol);
        self._highlighted.push(graphic);
      });
    },

    unhighlight: function (selector) {
      var self = this;
      var targets = selector
        ? this._labelsFromSelector(selector)
        : this._highlighted.slice();

      _.each(targets, function (graphic) {
        var symbol = graphic._oldSymbol;

        if (symbol) {
          graphic.setSymbol(symbol);
        }

        delete graphic._oldSymbol;
        _remove(self._highlighted, graphic);
      });
    },

    getDisplayNameField: function () {
      if (!this._displayField) {
        this._displayField = new Field(
          {
            id: "shape",
            name: "shape",
            label: "Shape",
          },
          {
            getter: function (model) {
              var currentShapeCount;

              if (_.isNumber(model._shapeCount)) {
                currentShapeCount = model._shapeCount;
              } else {
                currentShapeCount = model._shapeCount = ++shapeCount;
              }

              return currentShapeCount;
            },
          }
        );
      }

      return this._displayField;
    },

    getDisplayName: function (model) {
      if (this.getDisplayNameField()) {
        return this.getDisplayNameField().getValue(model);
      } else {
        return null;
      }
    },
  },
  {
    /**
     * Performs the necessary API calls to move the given geometry and its associated data from the `sourceLayer`,
     * where the model currently resides, to the `destinationLayer`.
     * @param {app/models/map/Geometry} geometry The geometry model to move
     * @param {app/models/map/EditableLayerBase} sourceLayer The layer containing the geometry to move.
     * @param {app/models/map/EditableLayerBase} destinationLayer The layer to which to move the geometry.
     * @param {Function} [beforeCommit] A hook to call before the save takes place. It gets passed a callback with
     * the expectation that an error will be sent as the first argument if an error occurs.
     * @param {Function} cb The callback function. First and only argument is an error, if any.
     */
    moveModel: function (
      geometry,
      sourceLayer,
      destinationLayer,
      beforeCommit,
      cb
    ) {
      var data = sourceLayer.remove(geometry);

      if (!cb) {
        cb = beforeCommit;
        beforeCommit = function (cb) {
          cb();
        };
      }

      if (!sourceLayer.featureLayer) {
        data = void 0; //only use data if layer has an associated feature layer.
      }

      var destinationModel = destinationLayer.add(geometry, data);

      geometry.originalLayer = destinationLayer;
      destinationModel.originalLayer = destinationLayer;

      //let's go through each of the fields in the source layer and copy that data to the fields in the new layer
      var sourceLayerFields = _(sourceLayer.getFields()).indexBy(function (
        field
      ) {
        return field.get("label");
      });
      var destinationLayerFields = _(destinationLayer.getFields()).indexBy(
        function (field) {
          return field.get("label");
        }
      );

      //Moves value from sourceField to destinationField
      function moveValueAcrossFields(source, destination) {
        var oldValue = source.getValue(data);

        //For some field types(e.g. select), can't just copy label or value.
        //Must determine which value in destinationField matches the value in sourceField.
        //TODO what other fields require this logic?
        if (
          destination.attributes.type == "select" ||
          destination.attributes.type == "radio"
        ) {
          var valueByLabel = _.find(source.attributes.values, function (value) {
            return (
              _.isObject(value) &&
              (value.value == oldValue || value.text == oldValue)
            );
          });

          if (valueByLabel) {
            var destinationValue = _.find(
              destination.attributes.values,
              function (value) {
                return _.isObject(value) && valueByLabel.text == value.text;
              }
            );

            if (destinationValue) {
              oldValue = destinationValue.value;
            }
          }
        }

        destination.setValue(destinationModel, oldValue);
      }

      _.each(sourceLayerFields, function (sourceField, key) {
        var destinationField = destinationLayerFields[key];

        if (destinationField) {
          moveValueAcrossFields(sourceField, destinationField);
        }
      });

      beforeCommit(function (err) {
        if (err) {
          return cb(err);
        }

        geometry.save(null, {
          success: function () {
            cb();
          },
          error: function (model, response) {
            destinationLayer.remove(geometry);
            sourceLayer.add(geometry, data);

            return cb(
              ErrorUtils.normalizeError(
                {
                  activity: "moving the geometry",
                },
                response
              )
            );
          },
        });
      });
    },
  }
);



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/EditableLayerBase.js
// module id = 135
// module chunks = 1